<script setup lang="ts">
import { computed, ref } from 'vue';
import type { DashboardEmail } from '@apparatix/schemas/DashboardEmails';
import { Stack, Typography } from '@apparatix/library/core';
import { useDashboardEmails } from '@apparatix/composables';
import EmailParticipantDetail from './EmailParticipantDetail.vue';
import EmailDate from './EmailDate.vue';
import UnsafeHtmlRenderer from '@apparatix/library/UnsafeHtmlRenderer.vue';
import EmailAttachmentDownloadButton from './EmailAttachmentDownloadButton.vue';

type Props = {
  email: DashboardEmail,
  hasLoadedInlineBody?: boolean,
  inlineBody?: string,
};

type Emits = {
  (e: 'collapse-click', emailId: number): void,
  (e: 'content-loaded', emailId: number, body?: string): void,
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { getInlineContent } = useDashboardEmails();

const shouldLoadImages = ref(false);
const isLoadingInlineBody = ref(false);
const didInlineBodyError = ref(false);

const renderedBody = computed(() => props.hasLoadedInlineBody ? props.inlineBody : props.email.body);
const rendererKey = computed(() => `${props.email.id}:${props.hasLoadedInlineBody ? 'inline' : 'original'}`);

const loadInlineBody = async () => {
  if (props.hasLoadedInlineBody) {
    shouldLoadImages.value = true;

    return;
  }

  isLoadingInlineBody.value = true;
  didInlineBodyError.value = false;

  try {
    const response = await getInlineContent(props.email.id);
    if (!response?.success) {
      didInlineBodyError.value = true;
      shouldLoadImages.value = false;

      return;
    }

    emit('content-loaded', props.email.id, response.body ?? '');
  } catch {
    didInlineBodyError.value = true;
    shouldLoadImages.value = false;
  } finally {
    isLoadingInlineBody.value = false;
  }
};

</script>
<template>
  <Stack :gap="2" grow>
    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <EmailParticipantDetail :participants="props.email.participants" :direction="props.email.direction" />
      <div class="flex items-center justify-between gap-3 sm:justify-end">
        <EmailAttachmentDownloadButton v-if="props.email.attachments?.length" :email="props.email" />
        <EmailDate :date="props.email.externalReceivedAt" />
      </div>
    </div>
    <div class="flex">
      <div class="grow pr-2 lg:pr-4">
        <Typography v-if="didInlineBodyError && !renderedBody" variant="body" muted>
          Unable to load message body.
        </Typography>
        <UnsafeHtmlRenderer
          v-else
          :key="rendererKey"
          v-model:load-images="shouldLoadImages"
          :unsafe-html="renderedBody ?? ''"
          :is-loading-images="isLoadingInlineBody"
          show-image-loader
          @load-images="loadInlineBody"
        />
      </div>
      <div class="flex-none">
        <span
          class="pi pi-minus cursor-pointer"
          style="color: var(--p-button-text-primary-color)"
          aria-label="Hide message details"
          @click="emit('collapse-click', props.email.id)"
        />
      </div>
    </div>
  </Stack>
</template>
